import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IErknmKindResultDecisionSelectOption } from 'CorePackage/CatalogsModule/interfaces/IErknmKindResultDecisionSelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ErknmResultDecisionsViolatedRequirementsModal } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisionsViolatedRequirementsModal';
import { ResultDecisionStatus } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ResultDecisionStatus';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmPmFormResultDecision } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormResultDecision';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { getRequirementsWithNonFixedViolations } from 'KnmErknmPackage/ErknmViolationsModule/common/getRequirementsWithNonFixedViolations';
import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { IKnmPmForm } from 'KnmPmPackage/MainModule/interfaces/IKnmPmForm';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { WAS_REMOVE_DUPLICATE_VIOLATIONS } from 'CorePackage/MainModule/constants/notices';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { KnmResultDecisionAgreementStatusEnum } from 'APIPackage/KnmModule/enums/KnmResultDecisionAgreementStatusEnum';

import { PmResultOfInspectionResultDecisionsFileInput } from './PmResultOfInspectionResultDecisionsFileInput';
import { PmResultOfInspectionPmResultDecisionsCollapseContent } from './PmResultOfInspectionPmResultDecisionsCollapseContent';
import { PmResultDecisionPrescriptionPaperless } from './PmResultDecisionPrescriptionPaperless/PmResultDecisionPrescriptionPaperless';
import { KnmLateFieldsRemark } from '../../../KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

interface IPmResultOfInspectionResultDecisionsCollapseProps {
  id: string;
  lateFieldName: string;
  name: string;
  onRemove: () => void;
  pmSubject?: IKnmErknmFormSubject;
  resultIndex: number;
  value: IKnmPmFormResultDecision;
  watchOnly: boolean;
  isPaperless: boolean;
  values?: IKnmPmForm;
  clearWatchOnly: boolean;
  isExpandAllInPrescription?: boolean;
}

interface IPmResultOfInspectionResultDecisionsCollapseStateProps {
  formRef: IKnmFormRef | undefined;
  settings: IAdminSetting[] | undefined;
}

const PmResultOfInspectionResultDecisionsCollapseWithoutStore = ({
  id,
  lateFieldName,
  name,
  onRemove,
  pmSubject,
  resultIndex,
  value,
  watchOnly,
  formRef,
  settings,
  isPaperless,
  values,
  clearWatchOnly,
  isExpandAllInPrescription,
}: IPmResultOfInspectionResultDecisionsCollapseProps & IPmResultOfInspectionResultDecisionsCollapseStateProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const isNewObject = value.isNew;
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const violatedRequirementsNpaFieldName = `${name}.violatedMandatoryRequirements`;
  const push = formRef?.current.mutators.push;
  const [isViolatedRequirementsNpaModalVisible, setIsViolatedRequirementsNpaModalVisible] = useState(false);
  const openViolatedRequirementsNpaModal = useCallback(() => {
    setIsViolatedRequirementsNpaModalVisible(true);
  }, []);
  const closeViolatedRequirementsNpaModal = useCallback(() => {
    setIsViolatedRequirementsNpaModalVisible(false);
  }, []);

  const addViolatedRequirementsNpa = (items: IKnmErknmFormViolatedRequirementsNpa[]) => {
    const requirements =
      // @ts-ignore
      (formRef?.current?.getFieldState(violatedRequirementsNpaFieldName)
        ?.value as IKnmErknmFormViolatedRequirementsNpa[]) || [];
    let hasFoundDuplicate = false;

    if (push) {
      items.forEach(item => {
        if (value.paperless) {
          const updatedItem = {
            ...item,
            violatedMandatory: item.violatedMandatory.map(vm => ({
              ...vm,
              violations: vm.violations?.map(vio => ({
                ...vio,
                fixPlanDates: [
                  {
                    isNew: true,
                    value: '',
                    guid: null,
                    dateCreate: null,
                  },
                ],
                fixPlanMethods: [],
                fixPlanExplanations: [],
              })),
            })),
          };

          const npaIndex = requirements.findIndex(
            req => req.npa.id === item.npa.id && req.structuralUnit.id === item.structuralUnit.id,
          );

          if (npaIndex > -1) {
            hasFoundDuplicate = true;
          } else {
            push(violatedRequirementsNpaFieldName, updatedItem);
          }
        } else {
          push(violatedRequirementsNpaFieldName, item);
        }
      });
    }

    if (hasFoundDuplicate) {
      dispatch(
        addNoticeAction({
          renderContent: WAS_REMOVE_DUPLICATE_VIOLATIONS,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.NPA_CHECK,
        }),
      );
    }
  };
  const requirementsNpa = formRef?.current.getFieldState('violatedMandatoryRequirements')?.value;
  const requirementsNpaAct = values?.act?.violatedMandatoryRequirements;
  const requirementsWithUnresolvedViolations = getRequirementsWithNonFixedViolations(
    requirementsNpa || requirementsNpaAct || [],
  );

  const isViolatedMandatoryRequirements = values?.act?.isViolatedMandatoryRequirements;
  const transformKindResultDecisionsSelectOptions = useCallback(
    (arr: IErknmKindResultDecisionSelectOption[] | undefined) => {
      return arr
        ?.filter(
          item =>
            isViolatedMandatoryRequirements?.value ||
            [
              KindResultDecisionCodesEnum.D_3,
              KindResultDecisionCodesEnum.D_4,
              KindResultDecisionCodesEnum.D_5,
              KindResultDecisionCodesEnum.D_8,
            ].includes(item.code),
        )
        .filter(item =>
          values?.act?.paperless &&
          (!values?.act.familiarizationNotifications || values?.act.familiarizationNotifications?.length === 0)
            ? item.code !== KindResultDecisionCodesEnum.D_1 && item.code !== KindResultDecisionCodesEnum.D_7
            : true,
        );
    },
    [values?.act],
  );

  const isPrescription =
    value.kindDecision?.value === KindResultDecisionCodesEnum.D_7 ||
    value.kindDecision?.value === KindResultDecisionCodesEnum.D_1;
  const [isRequirementsAutoFilled, setIsRequirementsAutoFilled] = useState<boolean>(false);
  const onAutoFillRequirements = (items: IKnmErknmFormViolatedRequirementsNpa[], fieldName: string) => {
    const requirementsWithNonFixedViolations = getRequirementsWithNonFixedViolations(items);

    push &&
      requirementsWithNonFixedViolations.forEach(requirement => {
        const resetedRequirement: IKnmErknmFormViolatedRequirementsNpa = {
          ...requirement,
          violatedMandatory:
            requirement.violatedMandatory.map(violatedMandatory => {
              return {
                ...violatedMandatory,
                violations: violatedMandatory.violations
                  ? violatedMandatory.violations.map(violation => {
                      return {
                        ...violation,
                        fixPlanMethods: null,
                        fixPlanExplanations: null,
                        fixFactMethod: null,
                        fixFactDate: null,
                        fixes: null,
                        // fixPlanDates: null,
                        fixPlanDates: [
                          {
                            isNew: true,
                            value: '',
                            guid: null,
                            dateCreate: null,
                          },
                        ],
                        fixPlanId: null,
                      };
                    })
                  : [],
              };
            }) || [],
        };

        push(fieldName, resetedRequirement);
      });
  };

  if (isPrescription && !!pmSubject && !value.violatedMandatoryRequirements?.length && !isRequirementsAutoFilled) {
    let requirements = requirementsWithUnresolvedViolations || [];

    if (isPaperless) {
      requirements = requirements.map(item => {
        return {
          ...item,
          violatedMandatory: item.violatedMandatory.map(vm => {
            return {
              ...vm,
              violations:
                vm.violations?.map(vio => ({
                  ...vio,
                  fixPlanDates: [
                    {
                      isNew: true,
                      value: '',
                      guid: null,
                      dateCreate: null,
                    },
                  ],
                })) || [],
            };
          }),
        };
      });
    }

    onAutoFillRequirements(requirements, violatedRequirementsNpaFieldName);
    setIsRequirementsAutoFilled(true);
  }

  const created = formRef?.current.getState().values.created;
  const isLaterThanViolationsInProd = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.VALIDATION_4_1_29_STARTS_FROM,
  );

  const isLater4111ForDecision = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.DECISION_SUBJECT_TYPE_4_1_1_START_FROM,
  );

  const handleChangeKind = useCallback(
    (v: IErknmKindResultDecisionSelectOption) => {
      if (values?.act?.paperless) {
        if (v?.code === KindResultDecisionCodesEnum.D_7 || v?.code === KindResultDecisionCodesEnum.D_1) {
          // @ts-ignore
          formRef?.current?.change(`${name}.paperless`, values?.act?.paperless);
          formRef?.current?.change(
            // @ts-ignore
            `${name}.number`,
            `${values?.act?.numberAct}-${values.pmResultDecisions?.length}`,
          );
        } else {
          // @ts-ignore
          formRef?.current?.change(`${name}.paperless`, false);
          // @ts-ignore
          formRef?.current?.change(`${name}.number`);
        }
      }
    },
    [formRef],
  );

  useEffect(() => {
    if (values?.act) {
      if (
        (value.kindDecision?.code === KindResultDecisionCodesEnum.D_7 ||
          value.kindDecision?.code === KindResultDecisionCodesEnum.D_1) &&
        (!values?.act?.familiarizationNotifications || values?.act?.familiarizationNotifications.length === 0)
      ) {
        formRef?.current.change(`${name}.kindDecision` as keyof IKnmPmForm);
        formRef?.current.change(`${name}.number` as keyof IKnmPmForm);
      }
    }
  }, [JSON.stringify(values?.act?.familiarizationNotifications)]);

  /**
   * Функция определения дополнительного статуса по соглашению
   */
  const agreementStatus = useMemo(() => {
    if (values?.agreements) {
      const agreement = values?.agreements.find(agr => agr.prescription?.id === value.guid && agr.isNew);

      if (agreement) return KnmResultDecisionAgreementStatusEnum.AGREEMENT_DRAFT;
    }

    return value.agreementStatus;
  }, [value, values?.agreements]);

  const hasError = checkHasErrorInCollapseBlock(formRef, name);

  return (
    <>
      <KnmCollapse
        id={id}
        theme="fields"
        title={
          !value.paperless ? 'Незамедлительные меры, принятые по недопущению причинению вреда (ущерба)' : 'Решение'
        }
        isNew={!value.paperless && isNewObject}
        subtitle={value.paperless ? value.number : undefined}
        openByDefault={isNewObject || isExpandAllInPrescription}
        onClose={!watchOnly && isNewObject ? onRemove : undefined}
        headerPostfix={
          isLaterThanViolationsInProd ? (
            <ResultDecisionStatus
              status={value.status}
              agreementStatus={agreementStatus}
              isNew={!!value.isNew}
              isPaperless={!!value.paperless}
            />
          ) : undefined
        }
        changeOpenedCallback={setIsKnmCollapseOpened}
        error={isKnmCollapseOpened && hasError}
      >
        <>
          {value.kindDecision && !isPaperless && (
            <>
              <Row>
                <FieldBlock
                  placementLabel="left"
                  size="half"
                  color="black"
                  label="Номер решения КНО"
                  id={`${id}.number`}
                  tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/number`} fieldId={`${id}.number`} />}
                >
                  <TextInputField
                    id={`${name}.number`}
                    name={`${name}.number`}
                    placeholder="Введите номер решения"
                    disabled={watchOnly}
                  />
                </FieldBlock>
                <FieldBlock
                  placementLabel="left"
                  size="half"
                  color="black"
                  label="Дата издания решения"
                  id={`${id}.date`}
                  tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/date`} fieldId={`${id}.date`} />}
                >
                  <DatePickerField
                    id={`${name}.date`}
                    name={`${name}.date`}
                    placeholder="Выберите дату издания решения"
                    disabled={watchOnly}
                  />
                </FieldBlock>
              </Row>
              <Row margin="none">
                <PmResultOfInspectionResultDecisionsFileInput
                  formRef={formRef}
                  name={`${name}.attachment`}
                  value={value}
                  watchOnly={watchOnly || !isNewObject}
                />
              </Row>
            </>
          )}
          <Row>
            <FieldBlock label="Вид решения">
              <SelectInputFieldAutoOptions
                name={`${name}.kindDecision`}
                size="medium"
                optionName={CommonCatalogsNamesEnum.ERKNM_KIND_RESULT_DECISION}
                transformFunc={transformKindResultDecisionsSelectOptions}
                isClearable={false}
                disabled={watchOnly || value.signed}
                onChange={handleChangeKind}
                warning={'Выбор значения "предписание" возможен после добавления уведомления об акте'}
              />
            </FieldBlock>
          </Row>
          {value.kindDecision &&
            (isPrescription && value.paperless ? (
              <PmResultDecisionPrescriptionPaperless
                value={value}
                watchOnly={watchOnly}
                name={name}
                addRequirementsNpa={openViolatedRequirementsNpaModal}
                values={values}
                resultIndex={resultIndex}
                id={id}
                clearWatchOnly={clearWatchOnly}
                isLater4111ForDecision={isLater4111ForDecision}
                lateFieldName={lateFieldName}
                isExpandAllInPrescription={isExpandAllInPrescription}
                extractVersion={value.extractVersion}
              />
            ) : (
              <PmResultOfInspectionPmResultDecisionsCollapseContent
                id={id}
                isPaperless={isPaperless}
                name={name}
                openViolatedRequirementsNpaModal={openViolatedRequirementsNpaModal}
                resultIndex={resultIndex}
                value={value}
                watchOnly={watchOnly}
                pmSubject={pmSubject}
                lateFieldName={lateFieldName}
                isExpandAllInPrescription={isExpandAllInPrescription}
              />
            ))}
        </>
      </KnmCollapse>
      <ErknmResultDecisionsViolatedRequirementsModal
        requirementsNpa={
          isPaperless ? values?.act?.violatedMandatoryRequirements : requirementsWithUnresolvedViolations
        }
        visible={isViolatedRequirementsNpaModalVisible}
        onClose={closeViolatedRequirementsNpaModal}
        onSubmit={addViolatedRequirementsNpa}
      />
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  formRef: getKnmFormRef(state),
  settings: getAdminFetchSettings(state),
});

export const PmResultOfInspectionPmResultDecisionsCollapse = connect(mapStateToProps)(
  PmResultOfInspectionResultDecisionsCollapseWithoutStore,
);
